import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { toBrisbaneInput, brisbaneInputToIso } from '../lib/brisbane';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { JobScope } from '../components/JobScope';
import { JobStart, JobNotes, JobAttachments, JobPhotoGroup, JobReady, JobCallback, JobCompletionNote, JobPrestartChecklist, JobRecover } from '../components/JobPhotos';
import { useVoice } from '../context/VoiceContext';
import { useAuth } from '../context/AuthContext';

// ─── Date helpers (Brisbane, UTC+10 year-round) ────────────────────
function todayBne(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Brisbane',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
function addDays(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}
function mondayOf(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dow = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7; // Mon = 0
  return addDays(ymd, -dow);
}
function addMonths(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1 + n, d)).toISOString().slice(0, 10);
}
/** Brisbane calendar date + time for an ISO instant. */
function bneParts(iso: string): { date: string; hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Brisbane',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
    .formatToParts(new Date(iso))
    .reduce((a: Record<string, string>, p) => ((a[p.type] = p.value), a), {});
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
  };
}
// Assign overlapping same-day events to side-by-side columns (like Google
// Calendar). Mutates copies with `col` (0-based) + `cols` (columns in the
// cluster); non-overlapping events get cols=1 (full width).
function layoutColumns(events: any[]): any[] {
  const evs = [...events].sort((a, b) => a.startDec - b.startDec || (a.startDec + a.durH) - (b.startDec + b.durH));
  let cluster: any[] = [];
  let clusterEnd = -Infinity;
  const commit = () => {
    const colEnds: number[] = [];
    for (const ev of cluster) {
      let placed = false;
      for (let c = 0; c < colEnds.length; c++) {
        if (ev.startDec >= colEnds[c] - 1e-6) { ev.col = c; colEnds[c] = ev.startDec + ev.durH; placed = true; break; }
      }
      if (!placed) { ev.col = colEnds.length; colEnds.push(ev.startDec + ev.durH); }
    }
    const n = colEnds.length || 1;
    for (const ev of cluster) ev.cols = n;
    cluster = [];
  };
  for (const ev of evs) {
    if (cluster.length && ev.startDec >= clusterEnd - 1e-6) { commit(); clusterEnd = -Infinity; }
    cluster.push(ev);
    clusterEnd = Math.max(clusterEnd, ev.startDec + ev.durH);
  }
  if (cluster.length) commit();
  return evs;
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function dayNum(ymd: string): string {
  return String(Number(ymd.split('-')[2]));
}
function fmtTime(h: number, m: number): string {
  const ap = h < 12 ? 'am' : 'pm';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return m ? `${hh}:${String(m).padStart(2, '0')}${ap}` : `${hh}${ap}`;
}

const HOUR_PX = 52;
const buttonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontWeight: 500,
  minHeight: 40,
  cursor: 'pointer',
};

// Remove Xero doc references (Quote QU-1114 / INV-1196) from a title — contractors
// see only the customer/job, not the financial reference.
function stripXeroRefs(title: string | null | undefined): string {
  if (!title) return 'Job';
  const t = String(title)
    .replace(/\b(quote|invoice)\s+(qu|inv)-?\d+/gi, '')
    .replace(/\b(qu|inv)-?\d+/gi, '')
    .replace(/\s*[-–]\s*$/, '')
    .replace(/^\s*[-–]\s*/, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return t || 'Job';
}

// Strip HTML tags and pull out the first URL (for showing event descriptions safely).
const aud = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
/** "Accepted" / "Sent" / … from Xero's upper-case quote status. */
function quoteStatusLabel(s: string | null | undefined): string {
  if (!s) return '';
  const t = s.toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function cleanDescription(desc: string | null | undefined): { text: string; url: string | null } {
  if (!desc) return { text: '', url: null };
  const url = desc.match(/https?:\/\/[^\s"'<>]+/)?.[0] ?? null;
  const text = desc
    .replace(/<br\s*\/?>/gi, '\n') // keep line breaks from HTML descriptions
    .replace(/<[^>]+>/g, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { text, url };
}

export function Calendar() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const voice = useVoice();
  const jobName = (title: string | null | undefined) => (isAdmin ? title ?? 'Job' : stripXeroRefs(title));
  // The "Call" button should read "Call <customer>", not the whole appointment
  // summary. Callback events are named "Callback - <customer> - <area>", so drop
  // the leading "Callback - " and the trailing " - <area>" to get just the name.
  const callName = (title: string | null | undefined) => {
    let n = jobName(title) || 'the customer';
    const m = n.match(/^\s*callbacks?\s*[-–]\s*(.+)$/i);
    if (m) n = m[1];
    n = n.split(/\s+[-–]\s+/)[0].trim();
    return n || 'the customer';
  };
  const [weekStart, setWeekStart] = useState(() => mondayOf(todayBne()));
  const [selected, setSelected] = useState<any | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [msgText, setMsgText] = useState<string | null>(null);
  const [msgCopied, setMsgCopied] = useState(false);
  const msgMutation = useMutation({
    mutationFn: (ws: string) => api.calendarScheduleMessage(ws),
    onSuccess: (r) => {
      setMsgCopied(false);
      setMsgText(r.text);
    },
  });
  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedAt, setReschedAt] = useState('');
  const [reschedHours, setReschedHours] = useState(3);
  // Inline address edit for the selected job (updates the calendar event location).
  const [editAddr, setEditAddr] = useState(false);
  const [addrDraft, setAddrDraft] = useState('');
  const addrMutation = useMutation({
    mutationFn: (v: { eventId: string; address: string }) => api.setJobAddress(v.eventId, v.address),
    onSuccess: (r) => {
      setSelected((prev: any) => (prev ? { ...prev, address: r.address } : prev));
      setEditAddr(false);
      queryClient.invalidateQueries({ queryKey: ['calendar-jobs'] });
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (eventId: string) => api.calendarDeleteEvent(eventId),
    onSuccess: () => {
      setConfirmDelete(false);
      setSelected(null);
      queryClient.invalidateQueries({ queryKey: ['calendar-jobs'] });
    },
  });
  const rescheduleMutation = useMutation({
    mutationFn: (v: { eventId: string; startTime: string; durationMins: number }) =>
      api.calendarReschedule(v.eventId, { startTime: v.startTime, durationMins: v.durationMins }),
    onSuccess: () => {
      setReschedOpen(false);
      setSelected(null);
      queryClient.invalidateQueries({ queryKey: ['calendar-jobs'] });
    },
  });
  // Split a job that can't be finished in one visit: shorten today's visit and
  // schedule the remainder at a chosen later time (a continuation event).
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitFirstEnd, setSplitFirstEnd] = useState(''); // datetime-local: today's visit ends
  const [splitContinueAt, setSplitContinueAt] = useState(''); // datetime-local: continuation starts
  const [splitHours, setSplitHours] = useState(3);
  const splitMutation = useMutation({
    mutationFn: (v: { eventId: string; firstEnd: string; continueStart: string; continueEnd: string }) =>
      api.splitJob(v.eventId, { firstEnd: v.firstEnd, continueStart: v.continueStart, continueEnd: v.continueEnd }),
    onSuccess: () => {
      setSplitOpen(false);
      setSelected(null);
      queryClient.invalidateQueries({ queryKey: ['calendar-jobs'] });
    },
  });
  // Merge a multi-day job (Day 1/Day 2…) back into a single job.
  const mergeMutation = useMutation({
    mutationFn: (eventId: string) => api.mergeJobDays(eventId),
    onSuccess: () => {
      setSelected(null);
      queryClient.invalidateQueries({ queryKey: ['calendar-jobs'] });
    },
  });
  // Complete-job flow: create + send the invoice (with the job's stored photos),
  // then recolour the card. Photos are uploaded in the on-site photo sections.
  const completeMutation = useMutation({
    mutationFn: (v: { eventId: string; verdict: 'up' | 'down' }) => api.calendarCompleteJob(v.eventId, [], v.verdict),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-jobs'] });
    },
  });
  // Mark done WITHOUT invoicing — for jobs already invoiced (portal or external).
  const completeNoInvoiceMutation = useMutation({
    mutationFn: (v: { eventId: string; verdict: 'up' | 'down' }) => api.calendarCompleteNoInvoice(v.eventId, v.verdict),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job-state'] });
    },
  });
  // Undo an accidental completion — move the job back to Booked.
  const revertMutation = useMutation({
    mutationFn: (eventId: string) => api.calendarRevertToBooked(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job-state'] });
    },
  });
  // Admin quality verdict (master gate). Must be chosen before a job can be
  // completed; thumbs up is blocked while the job has an open callback.
  const [verdict, setVerdict] = useState<'up' | 'down' | null>(null);
  const qualityQuery = useQuery({
    queryKey: ['job-quality', selected?.id],
    queryFn: () => api.getJobQuality(String(selected.id)),
    enabled: !!selected && isAdmin && selected.status !== 'COMPLETED',
  });
  const hasOpenCallback = !!qualityQuery.data?.hasOpenCallback;
  const closeComplete = () => {
    completeMutation.reset();
    completeNoInvoiceMutation.reset();
    revertMutation.reset();
    setVerdict(null);
  };
  // Leave address-edit / split mode when a different job is opened / modal closes.
  useEffect(() => { setEditAddr(false); addrMutation.reset(); setSplitOpen(false); splitMutation.reset(); setVerdict(null); }, [selected?.id]);
  const openSplit = (ev: any) => {
    setSplitFirstEnd(toBrisbaneInput(ev.end_time)); // default: today's visit ends at the current end (Brisbane)
    setSplitContinueAt('');
    setSplitHours(3);
    setReschedOpen(false);
    setSplitOpen(true);
  };

  // Pre-fill the reschedule form from the selected event, in BRISBANE time
  // (independent of the device's timezone).
  const openReschedule = (ev: any) => {
    setReschedAt(toBrisbaneInput(ev.start_time));
    const dur = Math.max(0.5, (new Date(ev.end_time).getTime() - new Date(ev.start_time).getTime()) / 3.6e6);
    setReschedHours(Math.round(dur * 2) / 2);
    setReschedOpen(true);
  };
  // Mon–Fri only — the business doesn't work weekends.
  const days = useMemo(() => Array.from({ length: 5 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const today = todayBne();

  const from = `${weekStart}T00:00:00+10:00`;
  const to = `${addDays(weekStart, 7)}T00:00:00+10:00`;
  const { data: allJobs = [], isLoading } = useQuery({
    queryKey: ['calendar-jobs', weekStart],
    queryFn: () => api.calendarJobs(from, to),
  });
  // Public holidays for the visible week (QLD). A Mon–Fri week can straddle a
  // year boundary, so fetch every year present in `days` and merge. Keyed as
  // YYYY-MM-DD strings, matching the day keys the grid renders with.
  const holidayYears = useMemo(() => Array.from(new Set(days.map((d) => d.slice(0, 4)))), [days]);
  const { data: holidayRows = [] } = useQuery({
    queryKey: ['public-holidays', holidayYears.join(',')],
    queryFn: async () => {
      const lists = await Promise.all(holidayYears.map((y) => api.getPublicHolidays({ year: Number(y) })));
      return lists.flat();
    },
  });
  const holidayByDay = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of holidayRows as any[]) {
      const k = String(h?.date ?? '').slice(0, 10);
      if (k) m.set(k, h?.name || 'Public holiday');
    }
    return m;
  }, [holidayRows]);
  // Optional ?customer= filter (deep link from a contact): show only that
  // customer's jobs this week.
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const customerFilter = (sp.get('customer') || '').toLowerCase();
  // Match the full name, or any of its words (≥3 chars) — a job is often titled
  // with just the first name (e.g. "Quote QU-1132 - April" for "April Booij").
  const filterWords = customerFilter.split(/\s+/).filter((w) => w.length >= 3);
  const jobs = customerFilter
    ? allJobs.filter((j: any) => {
        const n = (j.customer_name || '').toLowerCase();
        return n.includes(customerFilter) || filterWords.some((w) => n.includes(w));
      })
    : allJobs;

  // Decorate jobs with Brisbane day/time + duration; bucket by day.
  // Timed events position on the grid; all-day events go in a banner row.
  const byDay = useMemo(() => {
    const map: Record<string, any[]> = {};
    const allDay: Record<string, any[]> = {};
    for (const d of days) {
      map[d] = [];
      allDay[d] = [];
    }
    let minHour = 7;
    let maxHour = 17;
    for (const j of jobs) {
      if (j.all_day) {
        // All-day start is a plain YYYY-MM-DD (no timezone).
        const d = String(j.start_time).slice(0, 10);
        if (d in allDay) allDay[d].push(j);
        continue;
      }
      const s = bneParts(j.start_time);
      const e = bneParts(j.end_time);
      const startDec = s.hour + s.minute / 60;
      const durH = Math.max(0.5, (new Date(j.end_time).getTime() - new Date(j.start_time).getTime()) / 3.6e6);
      if (!(s.date in map)) continue;
      map[s.date].push({ ...j, startDec, durH, label: `${fmtTime(s.hour, s.minute)}–${fmtTime(e.hour, e.minute)}` });
      minHour = Math.min(minHour, Math.floor(startDec));
      maxHour = Math.max(maxHour, Math.ceil(startDec + durH));
    }
    // Lay out overlapping events side-by-side within each day.
    for (const d of days) map[d] = layoutColumns(map[d]);
    return { map, allDay, minHour: Math.max(0, minHour), maxHour: Math.min(24, maxHour) };
  }, [jobs, days]);

  const { map, allDay, minHour, maxHour } = byDay;
  const hasAllDay = days.some((d) => (allDay[d] ?? []).length > 0);
  const hours = Array.from({ length: maxHour - minHour }, (_, i) => minHour + i);
  const gridHeight = hours.length * HOUR_PX;
  const totalJobs = jobs.length;

  const last = days[days.length - 1];
  const rangeLabel = `${dayNum(days[0])} ${MONTHS[Number(days[0].split('-')[1]) - 1]} – ${dayNum(last)} ${MONTHS[Number(last.split('-')[1]) - 1]} ${last.split('-')[0]}`;

  return (
    <div style={{ padding: 'var(--spacing-4)' }}>
      {customerFilter && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)', padding: 'var(--spacing-2) var(--spacing-3)', background: 'var(--color-accent-muted)', borderRadius: 'var(--radius-default)', fontSize: 'var(--font-size-sm)', flexWrap: 'wrap' }}>
          <span>📌 Showing jobs for <strong>{sp.get('customer')}</strong>{jobs.length === 0 ? ' — none this week, use ‹ Week / Week › to find them' : ''}</span>
          <button onClick={() => setSp({}, { replace: true })} style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer', fontWeight: 600 }}>Clear filter</button>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-3)', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0 }}>Calendar</h1>
          <p style={{ color: 'var(--color-text-muted)', margin: 'var(--spacing-1) 0 0' }}>
            {rangeLabel} · {isLoading ? 'loading…' : `${totalJobs} job${totalJobs === 1 ? '' : 's'}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="date"
            value={weekStart}
            onChange={(e) => e.target.value && setWeekStart(mondayOf(e.target.value))}
            title="Jump to a date"
            style={{ ...buttonStyle, cursor: 'text', padding: '0 var(--spacing-2)' }}
          />
          <button style={buttonStyle} onClick={() => setWeekStart(mondayOf(addMonths(weekStart, -1)))} title="Previous month">‹‹ Month</button>
          <button style={buttonStyle} onClick={() => setWeekStart(addDays(weekStart, -7))} title="Previous week">‹ Week</button>
          <button style={buttonStyle} onClick={() => setWeekStart(mondayOf(today))}>Today</button>
          <button style={buttonStyle} onClick={() => setWeekStart(addDays(weekStart, 7))} title="Next week">Week ›</button>
          <button style={buttonStyle} onClick={() => setWeekStart(mondayOf(addMonths(weekStart, 1)))} title="Next month">Month ››</button>
          {isAdmin && (
            <button
              style={{ ...buttonStyle, background: 'var(--color-accent)', color: 'var(--color-text-inverse)', borderColor: 'var(--color-accent)' }}
              onClick={() => msgMutation.mutate(weekStart)}
              disabled={msgMutation.isPending}
              title="Generate a WhatsApp message for this week's jobs"
            >
              {msgMutation.isPending ? '…' : '💬 Schedule message'}
            </button>
          )}
        </div>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', background: 'var(--color-surface)' }}>
        <div style={{ minWidth: 760 }}>
          {/* Day headers */}
          <div style={{ display: 'grid', gridTemplateColumns: `56px repeat(5, 1fr)`, borderBottom: '1px solid var(--color-border)' }}>
            <div />
            {days.map((d, i) => {
              const holiday = holidayByDay.get(d);
              return (
              <div key={d} style={{ textAlign: 'center', padding: 'var(--spacing-2) 0', borderLeft: '1px solid var(--color-border)', background: holiday ? 'rgba(254,197,83,0.16)' : d === today ? 'var(--color-surface-elevated)' : 'transparent' }}>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{DAY_NAMES[i]}</div>
                <div style={{ fontWeight: 700, fontSize: 'var(--font-size-lg)', color: d === today ? 'var(--color-accent)' : 'var(--color-text)' }}>{dayNum(d)}</div>
                {holiday && (
                  <div title={holiday} style={{ margin: '3px auto 0', maxWidth: '92%', background: 'var(--color-gold, #FEC553)', color: 'var(--color-navy, #1B365D)', borderRadius: 4, padding: '1px 4px', fontSize: '10px', fontWeight: 700, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{holiday}</div>
                )}
              </div>
              );
            })}
          </div>

          {/* All-day banner row */}
          {hasAllDay && (
            <div style={{ display: 'grid', gridTemplateColumns: `56px repeat(5, 1fr)`, borderBottom: '1px solid var(--color-border)' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', padding: '4px 6px', textAlign: 'right' }}>all-day</div>
              {days.map((d) => (
                <div key={d} style={{ borderLeft: '1px solid var(--color-border)', padding: 3, minHeight: 24, background: holidayByDay.has(d) ? 'rgba(254,197,83,0.10)' : d === today ? 'rgba(0,176,151,0.04)' : 'transparent' }}>
                  {(allDay[d] ?? []).map((j) => (
                    <div key={j.id} onClick={() => setSelected(j)} title={`${jobName(j.customer_name)}${j.address ? ` · ${j.address}` : ''}`} style={{ background: 'var(--color-light-blue, #8DD2E5)', color: 'var(--color-navy, #1B365D)', borderRadius: 4, padding: '2px 6px', marginBottom: 2, fontSize: 'var(--font-size-xs)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>
                      {jobName(j.customer_name)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Time grid */}
          <div style={{ display: 'grid', gridTemplateColumns: `56px repeat(5, 1fr)`, position: 'relative' }}>
            {/* hour gutter */}
            <div>
              {hours.map((h) => (
                <div key={h} style={{ height: HOUR_PX, position: 'relative' }}>
                  <span style={{ position: 'absolute', top: -7, right: 6, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{fmtTime(h, 0)}</span>
                </div>
              ))}
            </div>
            {/* day columns */}
            {days.map((d) => (
              <div key={d} style={{ borderLeft: '1px solid var(--color-border)', position: 'relative', height: gridHeight, background: holidayByDay.has(d) ? 'rgba(254,197,83,0.10)' : d === today ? 'rgba(0,176,151,0.04)' : 'transparent' }}>
                {hours.map((h) => (
                  <div key={h} style={{ height: HOUR_PX, borderBottom: '1px solid var(--color-border)' }} />
                ))}
                {(map[d] ?? []).map((j) => {
                  const top = (j.startDec - minHour) * HOUR_PX;
                  const height = Math.max(j.durH * HOUR_PX - 2, 22);
                  const cols = j.cols || 1;
                  const col = j.col || 0;
                  const widthPct = 100 / cols;
                  return (
                    <div
                      key={j.id}
                      onClick={() => setSelected(j)}
                      title={`${jobName(j.customer_name)} · ${j.label}${j.address ? ` · ${j.address}` : ''}`}
                      style={{
                        position: 'absolute',
                        top,
                        left: `calc(${widthPct * col}% + 2px)`,
                        width: `calc(${widthPct}% - 4px)`,
                        height,
                        overflow: 'hidden',
                        background: j.status === 'COMPLETED' ? 'var(--color-success, #2e7d32)' : 'var(--color-accent)',
                        color: 'var(--color-text-inverse)',
                        borderRadius: 6,
                        padding: '3px 6px',
                        fontSize: 'var(--font-size-xs)',
                        boxShadow: 'var(--shadow-sm)',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{jobName(j.customer_name)}</div>
                      <div style={{ opacity: 0.9 }}>{j.label}</div>
                      {j.address && height > 48 && (
                        <div style={{ opacity: 0.9, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.address}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {!isLoading && totalJobs === 0 && (
        <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', marginTop: 'var(--spacing-4)' }}>
          No cleaning jobs scheduled this week. Use ‹ Prev / Next › to find a week with jobs, or schedule from Billing → Not yet scheduled.
        </p>
      )}

      {/* Event details modal */}
      {selected && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => { setReschedOpen(false); setSelected(null); }}
          style={{ position: 'fixed', inset: 0, background: 'var(--color-surface)', zIndex: 1000 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--color-surface)', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}
          >
            {/* Sticky header: title + always-visible close */}
            <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-3)', padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface)' }}>
              <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg, 20px)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{jobName(selected.customer_name)}</h2>
              <button onClick={() => { setReschedOpen(false); closeComplete(); setSelected(null); }} aria-label="Close" style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text)', fontSize: 22, lineHeight: 1, cursor: 'pointer' }}>✕</button>
            </div>
            {/* Scrollable body */}
            <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: 'var(--spacing-4)' }}>
            <div style={{ maxWidth: 880, margin: '0 auto' }}>
            {(() => {
              const s = selected.all_day ? null : bneParts(selected.start_time);
              const e = selected.all_day ? null : bneParts(selected.end_time);
              const when = selected.all_day
                ? `${String(selected.start_time).slice(0, 10)} · all day`
                : `${s!.date} · ${fmtTime(s!.hour, s!.minute)}–${fmtTime(e!.hour, e!.minute)}`;
              const { text, url } = cleanDescription(selected.description);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)', fontSize: 'var(--font-size-sm)' }}>
                  <div>🗓️ {when}</div>
                  {/* Quote total — admin only (the API omits `quote` for contractors,
                      who see neither the quote number nor the price). */}
                  {isAdmin && selected.quote && selected.quote.total != null && (
                    <div title="Quote total from Xero (includes GST)">
                      💲 <strong>{aud.format(selected.quote.total)}</strong>
                      <span style={{ color: 'var(--color-text-muted)' }}> inc GST{selected.quote.number ? ` · Quote ${selected.quote.number}` : ''}{selected.quote.status ? ` · ${quoteStatusLabel(selected.quote.status)}` : ''}</span>
                    </div>
                  )}
                  {editAddr ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span>📍</span>
                      <input
                        value={addrDraft}
                        onChange={(ev) => setAddrDraft(ev.target.value)}
                        placeholder="Job address"
                        style={{ flex: '1 1 260px', minWidth: 200, padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-background)', color: 'var(--color-text)' }}
                      />
                      <button onClick={() => addrMutation.mutate({ eventId: String(selected.id), address: addrDraft.trim() })} disabled={addrMutation.isPending} style={{ padding: '6px 12px', border: '1px solid var(--color-accent)', borderRadius: 'var(--radius-default)', background: 'var(--color-accent)', color: 'var(--color-text-inverse)', fontWeight: 600, cursor: 'pointer' }}>{addrMutation.isPending ? 'Saving…' : 'Save'}</button>
                      <button onClick={() => setEditAddr(false)} style={{ padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'transparent', color: 'var(--color-text)', cursor: 'pointer' }}>Cancel</button>
                      {addrMutation.isError && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>Couldn’t save the address.</span>}
                    </div>
                  ) : (selected.address || isAdmin) ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      📍{' '}
                      {selected.address ? (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selected.address)}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: 'var(--color-accent)', fontWeight: 600 }}
                          title="Open in Google Maps"
                        >
                          {selected.address}
                        </a>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>No address</span>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => { setAddrDraft(selected.address || ''); setEditAddr(true); }}
                          title="Change the job address"
                          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-accent)', fontWeight: 600, cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}
                        >
                          ✎ Edit
                        </button>
                      )}
                    </div>
                  ) : null}
                  {text && (
                    <div style={{ color: 'var(--color-text-muted)', whiteSpace: 'pre-wrap' }}>
                      {text.replace(url ?? '', '').trim()}
                    </div>
                  )}
                  {isAdmin && stripXeroRefs(selected.customer_name) !== 'Job' && (
                    <div>
                      <button
                        onClick={() => navigate(`/contacts?q=${encodeURIComponent(stripXeroRefs(selected.customer_name))}`)}
                        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-accent)', fontWeight: 600, cursor: 'pointer' }}
                        title="Open this customer's contact record"
                      >
                        👤 Open contact
                      </button>
                    </div>
                  )}
                  {isAdmin && url && (
                    <a href={url} target="_blank" rel="noreferrer" style={{ color: 'var(--color-accent)', fontWeight: 600 }}>
                      View in Xero ↗
                    </a>
                  )}
                  {isAdmin && selected.html_link && (
                    <a href={selected.html_link} target="_blank" rel="noreferrer" style={{ color: 'var(--color-accent)' }}>
                      Open in Google Calendar ↗
                    </a>
                  )}
                </div>
              );
            })()}

            {/* Admin note for the contractor, right below the address. */}
            {selected.id && <JobNotes eventId={String(selected.id)} isAdmin={isAdmin} />}
            {/* Pre-start checklist — under the notes, tick-to-dismiss. */}
            {selected.id && <JobPrestartChecklist eventId={String(selected.id)} />}
            {/* Reference photos/documents for the contractor. */}
            {selected.id && <JobAttachments eventId={String(selected.id)} isAdmin={isAdmin} />}

            {/* Call the customer without exposing their number: the call is placed
                through the in-portal softphone and the number is resolved on the
                server from this job, so subcontractors never see or dial it. */}
            {selected.id && voice && voice.status !== 'not-configured' && (
              <div style={{ marginTop: 'var(--spacing-3)' }}>
                <button
                  onClick={() => voice.callJob(String(selected.id), callName(selected.customer_name))}
                  disabled={voice.status !== 'ready' || voice.callState !== 'idle'}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: 'var(--spacing-2) var(--spacing-4)', border: '1px solid var(--color-accent)', borderRadius: 'var(--radius-default)', background: 'var(--color-accent)', color: 'var(--color-text-inverse)', fontWeight: 700, cursor: voice.status === 'ready' ? 'pointer' : 'not-allowed', fontSize: 'var(--font-size-sm)', opacity: voice.status === 'ready' ? 1 : 0.6 }}
                >
                  📞 {voice.callState !== 'idle' ? 'On call…' : `Call ${callName(selected.customer_name)}`}
                </button>
                <div style={{ marginTop: 4, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  {voice.status === 'connecting' ? 'Connecting the phone…'
                    : voice.status === 'ready' ? 'Calls the customer through the office line.'
                    : voice.status === 'error' ? 'Phone unavailable right now.'
                    : ''}
                </div>
              </div>
            )}

            {/* On-site job module — scope + before/after photos + ready-to-invoice,
                plus an admin-only Call Back (defects + defect photos). */}
            {selected.id && (
              <>
                <JobStart eventId={String(selected.id)} isAdmin={isAdmin} />
                {isAdmin && <JobRecover eventId={String(selected.id)} />}
                <JobScope eventId={String(selected.id)} />
                <JobPhotoGroup eventId={String(selected.id)} kind="before" title="📷 Before photos" />
                <JobPhotoGroup eventId={String(selected.id)} kind="after" title="📷 After photos" />
                <JobCompletionNote eventId={String(selected.id)} />
                <JobPhotoGroup eventId={String(selected.id)} kind="issue" title="📷 Photos what happened on the job" />
                <JobReady eventId={String(selected.id)} isAdmin={isAdmin} />
                {isAdmin && (
                  <JobCallback
                    eventId={String(selected.id)}
                    completed={selected.status === 'COMPLETED'}
                    onScheduled={(dt) => { setReschedOpen(false); closeComplete(); setSelected(null); setWeekStart(mondayOf(dt.slice(0, 10))); }}
                  />
                )}
              </>
            )}

            {deleteMutation.isError && (
              <p style={{ color: 'var(--color-error)' }}>{(deleteMutation.error as Error).message}</p>
            )}

            {/* Inline reschedule form (admin) */}
            {isAdmin && reschedOpen && (
              <div style={{ marginTop: 'var(--spacing-3)', padding: 'var(--spacing-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-surface-elevated)' }}>
                <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--spacing-1)' }}>New date &amp; time</label>
                <input type="datetime-local" value={reschedAt} onChange={(e) => setReschedAt(e.target.value)}
                  style={{ width: '100%', minHeight: 40, padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', marginBottom: 'var(--spacing-2)' }} />
                <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--spacing-1)' }}>Duration (hours)</label>
                <input type="number" min={0.5} max={40} step={0.5} value={reschedHours}
                  onChange={(e) => setReschedHours(Math.min(40, Math.max(0.5, Number(e.target.value) || 3)))}
                  style={{ width: '100%', minHeight: 40, padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)' }} />
                {reschedAt && holidayByDay.get(reschedAt.slice(0, 10)) && (
                  <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)', margin: '2px 0 0', fontWeight: 600 }}>
                    ⚠ {holidayByDay.get(reschedAt.slice(0, 10))} is a public holiday — pick another day.
                  </p>
                )}
                {rescheduleMutation.isError && (
                  <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>{(rescheduleMutation.error as Error).message}</p>
                )}
              </div>
            )}

            {/* Already-completed marker + undo (move back to Booked) */}
            {isAdmin && selected.status === 'COMPLETED' && !completeMutation.isSuccess && (
              <div style={{ marginTop: 'var(--spacing-3)' }}>
                {revertMutation.isSuccess ? (
                  <p style={{ fontWeight: 600, color: 'var(--color-accent)' }}>
                    ↩ Moved back to Booked{revertMutation.data?.docNumber ? ` (${revertMutation.data.docNumber})` : ''}. If this was invoiced by mistake, void that invoice in Xero.
                  </p>
                ) : (
                  <>
                    <p style={{ fontWeight: 600, color: 'var(--color-success, #2e7d32)', margin: '0 0 var(--spacing-2)' }}>✓ This job is marked completed.</p>
                    {revertMutation.isError && <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>{(revertMutation.error as Error).message}</p>}
                    <button
                      style={buttonStyle}
                      disabled={revertMutation.isPending}
                      title="Undo an accidental completion. Note: this does NOT void the Xero invoice — void it in Xero separately."
                      onClick={() => revertMutation.mutate(selected.id)}
                    >
                      {revertMutation.isPending ? 'Moving…' : '↩ Move back to Booked'}
                    </button>
                    <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', margin: '4px 0 0' }}>
                      If it was invoiced by mistake, also void that invoice in Xero.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Inline split form (admin) */}
            {isAdmin && splitOpen && (
              <div style={{ marginTop: 'var(--spacing-3)', padding: 'var(--spacing-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-surface-elevated)' }}>
                <p style={{ margin: '0 0 var(--spacing-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  Couldn’t finish today? Shorten this visit and book the rest as a second visit. The customer, scope and address carry over.
                </p>
                <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--spacing-1)' }}>Today’s visit ends</label>
                <input type="datetime-local" value={splitFirstEnd} onChange={(e) => setSplitFirstEnd(e.target.value)}
                  style={{ width: '100%', minHeight: 40, padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', marginBottom: 'var(--spacing-2)' }} />
                <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--spacing-1)' }}>Continue on</label>
                <input type="datetime-local" value={splitContinueAt} onChange={(e) => setSplitContinueAt(e.target.value)}
                  style={{ width: '100%', minHeight: 40, padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', marginBottom: 'var(--spacing-2)' }} />
                <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--spacing-1)' }}>Remaining duration (hours)</label>
                <input type="number" min={0.5} max={40} step={0.5} value={splitHours}
                  onChange={(e) => setSplitHours(Math.min(40, Math.max(0.5, Number(e.target.value) || 3)))}
                  style={{ width: '100%', minHeight: 40, padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)' }} />
                {splitMutation.isError && (
                  <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>{(splitMutation.error as Error).message}</p>
                )}
              </div>
            )}

            {/* Complete-job panel (admin): upload photos up front, then Complete job */}
            {isAdmin && !reschedOpen && !splitOpen && (selected.status !== 'COMPLETED' || completeMutation.isSuccess) && (
              <div style={{ marginTop: 'var(--spacing-3)', padding: 'var(--spacing-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-surface-elevated)' }}>
                {!completeMutation.isSuccess ? (
                  <>
                    <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', margin: 0 }}>
                      “Complete job” creates a Xero invoice due today from this job's quote, attaches the job's photos (before, after and what happened on the job), and emails it to the customer.
                    </p>
                    {completeMutation.isError && (
                      <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>{(completeMutation.error as Error).message}</p>
                    )}
                    {completeNoInvoiceMutation.isError && (
                      <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>{(completeNoInvoiceMutation.error as Error).message}</p>
                    )}
                    {completeNoInvoiceMutation.isSuccess && (
                      <p style={{ color: 'var(--color-success, #2e7d32)', fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>✓ Marked done (no invoice created). Close to refresh the calendar.</p>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 'var(--font-size-sm)' }}>
                    <p style={{ margin: '0 0 var(--spacing-1)', fontWeight: 600, color: 'var(--color-success, #2e7d32)' }}>
                      ✓ Invoice {completeMutation.data?.invoiceNumber ?? ''} created{completeMutation.data?.total != null ? ` ($${completeMutation.data.total})` : ''}.
                    </p>
                    <p style={{ margin: '0 0 var(--spacing-1)' }}>{completeMutation.data?.photosAttached ?? 0} photo(s) attached.</p>
                    <p style={{ margin: '0 0 var(--spacing-1)' }}>
                      {completeMutation.data?.emailed ? '📧 Emailed to the customer.' : `⚠️ Not emailed: ${completeMutation.data?.emailSkippedReason ?? 'unknown'}`}
                    </p>
                    <p style={{ margin: '0 0 var(--spacing-1)' }}>
                      {completeMutation.data?.smsSent ? '📱 SMS sent with the invoice attached.' : `⚠️ SMS not sent: ${completeMutation.data?.smsSkippedReason ?? 'unknown'}`}
                    </p>
                    {completeMutation.data?.invoiceUrl && (
                      <a href={completeMutation.data.invoiceUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--color-accent)' }}>View invoice in Xero →</a>
                    )}
                  </div>
                )}
              </div>
            )}

            {mergeMutation.isError && (
              <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)', textAlign: 'right', margin: '4px 0 0' }}>{(mergeMutation.error as Error).message}</p>
            )}
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end', marginTop: 'var(--spacing-4)' }}>
              {isAdmin && reschedOpen ? (
                <>
                  <button
                    style={{ ...buttonStyle, background: 'var(--color-accent)', color: 'var(--color-text-inverse)', borderColor: 'var(--color-accent)' }}
                    disabled={!reschedAt || !!holidayByDay.get(reschedAt.slice(0, 10)) || rescheduleMutation.isPending}
                    onClick={() =>
                      rescheduleMutation.mutate({
                        eventId: selected.id,
                        startTime: brisbaneInputToIso(reschedAt),
                        durationMins: Math.round(reschedHours * 60),
                      })
                    }
                  >
                    {rescheduleMutation.isPending ? 'Saving…' : 'Save new time'}
                  </button>
                  <button style={buttonStyle} onClick={() => setReschedOpen(false)}>Cancel</button>
                </>
              ) : isAdmin && splitOpen ? (
                <>
                  <button
                    style={{ ...buttonStyle, background: 'var(--color-accent)', color: 'var(--color-text-inverse)', borderColor: 'var(--color-accent)' }}
                    disabled={!splitFirstEnd || !splitContinueAt || splitMutation.isPending}
                    onClick={() => {
                      const continueStartIso = brisbaneInputToIso(splitContinueAt);
                      const continueStart = new Date(continueStartIso);
                      splitMutation.mutate({
                        eventId: selected.id,
                        firstEnd: brisbaneInputToIso(splitFirstEnd),
                        continueStart: continueStartIso,
                        continueEnd: new Date(continueStart.getTime() + splitHours * 3.6e6).toISOString(),
                      });
                    }}
                  >
                    {splitMutation.isPending ? 'Splitting…' : 'Split job'}
                  </button>
                  <button style={buttonStyle} onClick={() => setSplitOpen(false)}>Cancel</button>
                </>
              ) : completeMutation.isSuccess ? (
                <button
                  style={{ ...buttonStyle, background: 'var(--color-accent)', color: 'var(--color-text-inverse)', borderColor: 'var(--color-accent)' }}
                  onClick={() => { closeComplete(); setSelected(null); }}
                >
                  Done
                </button>
              ) : (
                <>
                  {isAdmin && selected.status !== 'COMPLETED' && !completeNoInvoiceMutation.isSuccess && (
                    <div style={{ width: '100%', marginBottom: 'var(--spacing-2)' }}>
                      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 4 }}>How was this job? (required to complete)</div>
                      <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                        <button
                          type="button"
                          onClick={() => setVerdict('up')}
                          disabled={hasOpenCallback}
                          title={hasOpenCallback ? 'This job has an open callback — resolve it before marking the job good.' : 'Job went well'}
                          style={{ ...buttonStyle, flex: 1, opacity: hasOpenCallback ? 0.5 : 1, background: verdict === 'up' ? 'var(--color-success, #2e7d32)' : 'var(--color-surface)', color: verdict === 'up' ? '#fff' : 'var(--color-text)', borderColor: verdict === 'up' ? 'var(--color-success, #2e7d32)' : 'var(--color-border)' }}
                        >
                          👍 Good
                        </button>
                        <button
                          type="button"
                          onClick={() => setVerdict('down')}
                          title="Something was off — never ask this customer for a review"
                          style={{ ...buttonStyle, flex: 1, background: verdict === 'down' ? 'var(--color-error)' : 'var(--color-surface)', color: verdict === 'down' ? '#fff' : 'var(--color-text)', borderColor: verdict === 'down' ? 'var(--color-error)' : 'var(--color-border)' }}
                        >
                          👎 Not good
                        </button>
                      </div>
                      {hasOpenCallback && (
                        <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)', margin: '4px 0 0' }}>This job has an open callback — resolve it before you can mark it good.</p>
                      )}
                      {verdict === null && !hasOpenCallback && (
                        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)', margin: '4px 0 0' }}>Choose 👍 or 👎 to enable completing this job.</p>
                      )}
                    </div>
                  )}
                  {isAdmin && selected.status !== 'COMPLETED' && !completeNoInvoiceMutation.isSuccess && (
                    <button
                      style={{ ...buttonStyle, background: 'var(--color-success, #2e7d32)', color: '#fff', borderColor: 'var(--color-success, #2e7d32)', opacity: verdict === null ? 0.5 : 1 }}
                      disabled={completeMutation.isPending || completeNoInvoiceMutation.isPending || verdict === null}
                      onClick={() => verdict && completeMutation.mutate({ eventId: selected.id, verdict })}
                    >
                      {completeMutation.isPending ? 'Creating invoice…' : '✓ Complete job & invoice'}
                    </button>
                  )}
                  {isAdmin && selected.status !== 'COMPLETED' && !completeNoInvoiceMutation.isSuccess && (
                    <button
                      style={{ ...buttonStyle, opacity: verdict === null ? 0.5 : 1 }}
                      disabled={completeMutation.isPending || completeNoInvoiceMutation.isPending || verdict === null}
                      title="Already invoiced this customer? Close the job without creating a second invoice."
                      onClick={() => verdict && completeNoInvoiceMutation.mutate({ eventId: selected.id, verdict })}
                    >
                      {completeNoInvoiceMutation.isPending ? 'Marking done…' : '✓ Mark done (already invoiced)'}
                    </button>
                  )}
                  {isAdmin && (
                    <button style={buttonStyle} disabled={completeMutation.isPending} onClick={() => openReschedule(selected)}>Reschedule</button>
                  )}
                  {isAdmin && selected.status !== 'COMPLETED' && !selected.all_day && (
                    <button style={buttonStyle} disabled={completeMutation.isPending} onClick={() => openSplit(selected)} title="Finish part now and book the rest for later">Split job</button>
                  )}
                  {isAdmin && /\(day \d+ of \d+\)|\(continued\)/i.test(String(selected.customer_name || '')) && (
                    <button
                      style={buttonStyle}
                      disabled={mergeMutation.isPending || completeMutation.isPending}
                      title="Undo a multi-day split — combine the days back into a single job"
                      onClick={() => mergeMutation.mutate(selected.id)}
                    >
                      {mergeMutation.isPending ? 'Merging…' : '⇄ Merge into one job'}
                    </button>
                  )}
                  {isAdmin && (
                    <button style={{ ...buttonStyle, color: 'var(--color-error)', borderColor: 'var(--color-error)' }} disabled={completeMutation.isPending} onClick={() => setConfirmDelete(true)}>
                      Delete
                    </button>
                  )}
                  <button style={buttonStyle} disabled={completeMutation.isPending} onClick={() => { setReschedOpen(false); closeComplete(); setSelected(null); }}>Close</button>
                </>
              )}
            </div>
            </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDelete}
        title="Delete this event?"
        message={`This removes "${selected?.customer_name ?? 'the event'}" from Google Calendar${selected?.source === 'google' ? ' and the linked booking' : ''}. This cannot be undone.`}
        tone="danger"
        confirmLabel={deleteMutation.isPending ? 'Deleting…' : 'Delete'}
        onConfirm={() => selected && deleteMutation.mutate(selected.id)}
        onCancel={() => setConfirmDelete(false)}
      />

      {/* Weekly schedule message (WhatsApp) */}
      {msgText !== null && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setMsgText(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--spacing-4)' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: 'var(--spacing-4)', width: 'min(560px, 96vw)', boxShadow: 'var(--shadow-sm)' }}
          >
            <h2 style={{ marginTop: 0 }}>Schedule message</h2>
            <p style={{ color: 'var(--color-text-muted)', marginTop: 0, fontSize: 'var(--font-size-sm)' }}>
              Edit the team and any job notes, then copy and paste into WhatsApp.
            </p>
            <textarea
              value={msgText}
              onChange={(e) => setMsgText(e.target.value)}
              rows={16}
              style={{ width: '100%', fontFamily: 'inherit', fontSize: 'var(--font-size-sm)', padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', resize: 'vertical', whiteSpace: 'pre-wrap' }}
            />
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end', marginTop: 'var(--spacing-3)' }}>
              <button
                style={{ ...buttonStyle, background: 'var(--color-accent)', color: 'var(--color-text-inverse)', borderColor: 'var(--color-accent)' }}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(msgText);
                    setMsgCopied(true);
                  } catch {
                    setMsgCopied(false);
                  }
                }}
              >
                {msgCopied ? 'Copied ✓' : 'Copy'}
              </button>
              <button style={buttonStyle} onClick={() => setMsgText(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
